<script setup lang="ts">
  import { z } from 'zod';
  import { isUndefined } from '@borg/utils';
  import { MpFile } from '@borg/types';
  import {
    MpButton,
    MpIcon,
    MpInput,
    MpModal,
    MpSelect,
    MpSpinner,
    openMpConfirmModal,
    pushToastMessage,
  } from '@borg/ui';
  import DocumentCard from '@/components/DocumentsList/DocumentCard.vue';
  import FileList from '@/components/DocumentsList/FileList.vue';
  import { generateProfilePdf } from '@/layers/MyProfile/components/GenerateProfilePdf.vue';

  const props = withDefaults(
    defineProps<{
      /**
       * List of selected files
       */
      modelValue?: string[];
      /**
       * List of files
       */
      files: MpFile[];
      /**
       * Files for upload
       */
      filesForUpload?: FileList;
      /**
       * If files are selectable
       */
      selectable?: boolean;
      /**
       * Determines if we're showing a checkbox or a radio button
       */
      multiSelectable?: boolean;
      /**
       * If select inputs are disabled
       */
      selectDisabled?: boolean;
      /**
       * If files are downloadable
       */
      downloadable?: boolean;
      /**
       * Source of files
       */
      source: 'document' | 'cv';
      /**
       * If subtitle should be shown
       */
      showSubtitle?: boolean;
      /**
       * If context menu should be shown
       */
      showContextMenu?: boolean;
      /**
       * Special action on preview click
       */
      onPreview?: () => void;
      /**
       * On 'Edit', enables document source change
       */
      enableSourceChange?: boolean;
      /**
       * Whether to show the files that are in the process of uploading
       */
      showUploadingFiles?: boolean;
    }>(),
    {
      modelValue: undefined,
      files: () => [],
      filesForUpload: undefined,
      selectable: false,
      selectDisabled: false,
      downloadable: true,
      selected: () => [],
      showSubtitle: true,
      showContextMenu: true,
      onPreview: undefined,
      enableSourceChange: false,
      showUploadingFiles: true,
    },
  );

  const emit = defineEmits<{
    'update:modelValue': [selected: string[]];
    'update:files': [files: MpFile[]];
    fileDeleted: [file: MpFile];
    fileUploadStarted: [file: MpFile];
    fileUploaded: [file: MpFile];
    fileEdited: [file: MpFile];
    toggle: [file: MpFile];
    'upload-complete': [];
  }>();

  defineSlots<{
    beforeTitle(props: Record<string, unknown>): void;
    afterTitle(props: Record<string, unknown>): void;
    Title(props: Record<string, unknown>): void;
  }>();

  const { t } = useI18n();

  const fileSources = [
    { value: 'cv', label: t('DOCUMENTS_LIST.SOURCE.CV') },
    { value: 'document', label: t('DOCUMENTS_LIST.SOURCE.DOCUMENT') },
  ];

  const { files: _filesForUpload, upload } = useFileUpload({ source: props.source });
  const isPreviewModalOpened = ref(false);
  const fileToBePreviewed = ref<MpFile>();
  const fileToBeDownloaded = ref<MpFile>();
  const isEditModalOpened = ref(false);
  const fileToBeEdited = ref<MpFile>();
  const isEditInProgress = ref(false);

  const isUploading = computed(() => _filesForUpload.value && _filesForUpload.value.length > 0);

  const { values, state, validate, resetStates, setValues } = useForm({
    initialData: () => ({
      name: '',
      source: { value: '', label: '' },
    }),
    schema: z.object({
      name: z.string().min(1, t('REQUIRED_INPUT')),
      source: z.object(
        { value: z.string(), label: z.string() },
        { invalid_type_error: t('REQUIRED_INPUT'), required_error: t('REQUIRED_INPUT') },
      ),
    }),
  });

  function getFileShortName(file: MpFile) {
    if (file.name.length > 20) {
      return `${file.name.substring(0, 20)}...`;
    }

    return file.name;
  }

  async function onUploadFiles(fileList?: FileList) {
    if (!fileList) return;

    const { onError, onSuccess, onDone, onStart } = upload(fileList);

    onStart((file) => {
      emit('fileUploadStarted', file);
    });

    onSuccess((file) => {
      emit('fileUploaded', file);
      emit('update:files', [file, ...props.files]);
    });

    onDone(async (files) => {
      emit(
        'update:modelValue',
        files.map((file) => file.id),
      );
      emit('upload-complete');
    });

    onError((file) => {
      emit(
        'update:files',
        props.files?.filter((f) => f.id !== file.id),
      );
      pushToastMessage({
        type: 'alert',
        title: t('DOCUMENTS_LIST.UPLOAD_ERROR_TOAST.TITLE'),
        subtitle: t('DOCUMENTS_LIST.UPLOAD_ERROR_TOAST.SUBTITLE', {
          file: getFileShortName(file),
        }),
      });
    });
  }

  function onOpenPreviewModal(file: MpFile) {
    fileToBePreviewed.value = file;
    isPreviewModalOpened.value = true;
  }

  async function onOpenDownloadModal(file: MpFile) {
    fileToBeDownloaded.value = file;
  }

  function onOpenEditModal(file: MpFile) {
    resetStates();
    isEditModalOpened.value = true;
    fileToBeEdited.value = file;
    const source = fileSources.find((fs) => fs.value === file.source);
    setValues({ name: file.name, source });
  }

  async function onConfirmEdit() {
    const isEditFormValid = await validate();

    if (isEditFormValid && fileToBeEdited.value) {
      isEditInProgress.value = true;
      const editedFile = (await myDocumentsRepository.update({
        id: fileToBeEdited.value.id,
        name: values.name,
        source: values.source.value,
      })) as MpFile;
      isEditInProgress.value = false;

      if (editedFile) {
        emit('fileEdited', editedFile);
        emit(
          'update:files',
          props.files?.map((file) => (file.id === fileToBeEdited.value?.id ? editedFile : file)),
        );
      }
      isEditModalOpened.value = false;
    }
  }

  function onCancelEdit() {
    isEditModalOpened.value = false;
  }

  async function onOpenDeleteModal(file: MpFile) {
    const confirmDelete = await openMpConfirmModal({
      title: t('DOCUMENTS_LIST.DELETE_CONFIRM_MODAL.TITLE', { file: file.name }),
      confirmButtonLabel: t('DOCUMENTS_LIST.DELETE_CONFIRM_MODAL.CONFIRM_BUTTON_LABEL'),
      cancelButtonLabel: t('CANCEL'),
    });

    if (confirmDelete) {
      try {
        await myDocumentsRepository.delete(file.id);
        emit('fileDeleted', file);
        emit(
          'update:files',
          props.files?.filter((f) => f.id !== file.id),
        );
      } catch (error) {
        pushToastMessage({
          type: 'alert',
          title: t('DOCUMENTS_LIST.DELETE_ERROR_TOAST.TITLE'),
          subtitle: t('DOCUMENTS_LIST.DELETE_ERROR_TOAST.SUBTITLE', { file: file.name }),
        });
      }
    }
  }

  function onPreviewAction(file: MpFile) {
    if (!isUndefined(props.onPreview)) {
      props.onPreview();
    } else if (props.downloadable) {
      onOpenDownloadModal(file);
    } else {
      onOpenPreviewModal(file);
    }
  }

  watch(() => props.filesForUpload, onUploadFiles);
</script>

<template>
  <FileList>
    <template v-if="showUploadingFiles">
      <DocumentCard
        v-for="file in _filesForUpload"
        :key="file.id"
        :file="file"
        :progress="file.progress"
        :selectable="selectable"
        :multi-selectable="multiSelectable"
        :disabled="selectDisabled"
      />
    </template>

    <MpSpinner
      v-else-if="isUploading"
      class="centerd-spinner"
    />

    <DocumentCard
      v-for="file in files"
      :key="file.id"
      :file="file"
      :selectable="selectable"
      :multi-selectable="multiSelectable"
      :disabled="selectDisabled"
      :checked="modelValue?.includes(file.id)"
      :show-subtitle="showSubtitle"
      :show-context-menu="showContextMenu"
      @edit="onOpenEditModal"
      @delete="onOpenDeleteModal"
      @preview="onPreviewAction"
      @toggle="(f) => emit('toggle', f)"
    >
      <template
        v-if="!!$slots.beforeTitle"
        #beforeTitle
      >
        <slot name="beforeTitle" />
      </template>
      <template
        v-if="!!$slots.afterTitle"
        #afterTitle
      >
        <slot name="afterTitle" />
      </template>
    </DocumentCard>
  </FileList>
  <MpModal
    v-model:is-open="isPreviewModalOpened"
    :title="fileToBePreviewed?.name"
    :close-button="false"
  >
    <img
      v-if="fileToBePreviewed?.previewUrl"
      :src="fileToBePreviewed.previewUrl"
      :alt="fileToBePreviewed?.name"
    />
    <MpIcon
      v-else
      icon="visibility_off"
      style="text-align: center; width: 100%"
    />
    <MpButton
      :text="$t('DOWNLOAD')"
      left-icon="download"
      @click="generateProfilePdf()"
    />
    <MyProfileGenerateProfilePdf />
  </MpModal>
  <DocumentsListDownloadModal
    :is-open="!!fileToBeDownloaded"
    :file="fileToBeDownloaded"
    @close="fileToBeDownloaded = undefined"
  />
  <SiteActionsModal
    v-model:is-open="isEditModalOpened"
    :title="$t('DOCUMENTS_LIST.EDIT_MODAL.TITLE')"
    :in-progress="isEditInProgress"
    size="sm"
    @confirm="onConfirmEdit"
    @cancel="onCancelEdit"
  >
    <div class="modal-form">
      <MpInput
        v-model="values.name"
        :label="$t('DOCUMENTS_LIST.EDIT_MODAL.NAME_LABEL')"
        :placeholder="$t('DOCUMENTS_LIST.EDIT_MODAL.NAME_PLACEHOLDER')"
        :readonly="isEditInProgress"
        :error="state.name.message"
        required
      />
      <MpSelect
        v-if="enableSourceChange"
        v-model="values.source"
        :label="$t('DOCUMENTS_LIST.EDIT_MODAL.SOURCE_LABEL')"
        :placeholder="$t('DOCUMENTS_LIST.EDIT_MODAL.SOURCE_PLACEHOLDER')"
        :readonly="isEditInProgress"
        :error="state.source.message"
        :options="fileSources"
        required
      />
    </div>
  </SiteActionsModal>
</template>

<style scoped lang="scss">
  .modal-form {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: var(--mp-space-50);
  }

  .centerd-spinner {
    display: block;
    margin: 20px auto;
  }
</style>
